import {
	LibraryVocabulary,
	PracticeSet,
	PracticeSetAnswer,
	PracticeSetItemChoice,
	PracticeVocabularyLearningProgressType,
} from '@/models.ts'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button.tsx'
import * as React from 'react'
import AnkiResultCardStack from '@/components/anki-result-card-stack.tsx'
import { ChevronLeft, ChevronRight, CircleCheckBig, CircleX, Loader2 } from 'lucide-react'
import { useTheme } from '@/useTheme.ts'
import { toast } from 'sonner'
import api from '@/api.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import PracticeSetResultSummary from '@/components/practice-set-result-summary.tsx'
import LibraryVocabularyLearningsPracticeResult from '@/components/library-vocabulary-learnings-practice-result.tsx'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'

interface PracticeItemChoicesResult {
	is_correct: boolean
	previous_learning_stage: number
	new_learning_stage: number
	library_vocabulary: LibraryVocabulary
	learning_progress_type: PracticeVocabularyLearningProgressType
}

interface ChoicesPracticeAnswer extends PracticeSetAnswer {
	result: Record<number, PracticeItemChoicesResult>
}

interface ChoicesPracticeProps {
	practiceSet: PracticeSet
	practiceSetItems: PracticeSetItemChoice[]
	onPracticeAnswersSubmitted: () => void
	onClosePracticeSet: () => void
	answerResultCompleted: any
}

interface PracticeSetItemChoicePlus extends PracticeSetItemChoice {
	all_choices: string[]
}

interface CurrentCardSelection {
	id: number
	selected: string
	correct: string
}

const ChoicesPractice = ({
	practiceSet,
	practiceSetItems,
	onPracticeAnswersSubmitted,
	onClosePracticeSet,
	answerResultCompleted,
}: ChoicesPracticeProps) => {
	const { t } = useTranslation()
	const { themeColor } = useTheme()
	const { currentPracticeStatus, availableAppLanguages, appLanguageCode } = useGlobalStore()
	const [cards, setCards] = useState<PracticeSetItemChoicePlus[]>(
		practiceSetItems.map((item) => ({
			...item,
			all_choices: [item.vocabulary.word, ...item.other_vocabulary_choices].sort(() => Math.random() - 0.5),
		})),
	)
	const [answers, setAnswers] = useState<Record<number, CurrentCardSelection>>({})
	const [isSubmittingAnswersLoading, setIsSubmittingAnswersLoading] = useState(false)
	const [answerResult, setAnswerResult] = useState<ChoicesPracticeAnswer>(answerResultCompleted || undefined)
	const { showModal, setMode, setData } = useWordDetailsModalStore()

	const answerDetails = useMemo(() => {
		const correctCount = Object.values(answerResult?.result || {}).filter((x) => x.is_correct).length
		const incorrectCount = Object.values(answerResult?.result || {}).filter((x) => !x.is_correct).length
		const justLearnedVocab =
			Object.values(answerResult?.result || {}).filter(
				(x) => x.learning_progress_type === PracticeVocabularyLearningProgressType.JUST_LEARNED,
			) ?? []
		const improvedLearningsVocab =
			Object.values(answerResult?.result || {}).filter(
				(x) => x.learning_progress_type === PracticeVocabularyLearningProgressType.IMPROVED_LEARNING,
			) ?? []
		const otherVocab = Object.values(answerResult?.result || {}).filter(
			(x) =>
				x.learning_progress_type !== PracticeVocabularyLearningProgressType.JUST_LEARNED &&
				x.learning_progress_type !== PracticeVocabularyLearningProgressType.IMPROVED_LEARNING,
		)

		return {
			correctCount: correctCount,
			incorrectCount: incorrectCount,
			justLearnedVocab: justLearnedVocab,
			improvedLearningsVocab: improvedLearningsVocab,
			otherVocab: otherVocab,
		}
	}, [answerResult])

	// Configuration: Change these values to adjust card spacing
	const CARD_INSET_X = 32 // pixels - horizontal spacing (left/right)
	const CARD_INSET_Y = 128 // pixels - vertical spacing (top/bottom)

	// Calculate dynamic offset based on vertical inset value
	const cardOffset = useMemo(() => {
		// Base offset percentage + additional offset based on vertical inset
		// The more vertical inset, the higher the offset needed
		const baseOffset = 110
		const additionalOffset = (CARD_INSET_Y / 8) * 5 // 5% per 8px of vertical inset
		return baseOffset + additionalOffset
	}, [CARD_INSET_Y])

	const insetStyles = useMemo(() => {
		const tailwindInsetsX = {
			8: 'inset-x-2',
			16: 'inset-x-4',
			24: 'inset-x-6',
			32: 'inset-x-8',
			40: 'inset-x-10',
			48: 'inset-x-12',
			64: 'inset-x-16',
			80: 'inset-x-20',
			96: 'inset-x-24',
		}

		const tailwindInsetsY = {
			0: 'inset-y-0',
			1: 'inset-y-px',
			2: 'inset-y-0.5',
			4: 'inset-y-1',
			6: 'inset-y-1.5',
			8: 'inset-y-2',
			10: 'inset-y-2.5',
			12: 'inset-y-3',
			14: 'inset-y-3.5',
			16: 'inset-y-4',
			20: 'inset-y-5',
			24: 'inset-y-6',
			28: 'inset-y-7',
			32: 'inset-y-8',
			36: 'inset-y-9',
			40: 'inset-y-10',
			44: 'inset-y-11',
			48: 'inset-y-12',
			56: 'inset-y-14',
			64: 'inset-y-16',
			72: 'inset-y-18',
			80: 'inset-y-20',
			88: 'inset-y-22',
			96: 'inset-y-24',
			112: 'inset-y-28',
			128: 'inset-y-32',
			144: 'inset-y-36',
			160: 'inset-y-40',
			176: 'inset-y-44',
			192: 'inset-y-48',
			208: 'inset-y-52',
			224: 'inset-y-56',
			240: 'inset-y-60',
			256: 'inset-y-64',
			288: 'inset-y-72',
			320: 'inset-y-80',
			384: 'inset-y-96',
		}

		const xClass = tailwindInsetsX[CARD_INSET_X]
		const yClass = tailwindInsetsY[CARD_INSET_Y]

		return {
			tailwindClasses: xClass && yClass ? `${xClass} ${yClass}` : null,
			customStyles:
				!xClass || !yClass
					? {
							top: CARD_INSET_Y,
							right: CARD_INSET_X,
							bottom: CARD_INSET_Y,
							left: CARD_INSET_X,
						}
					: {},
		}
	}, [CARD_INSET_X, CARD_INSET_Y])
	const fadeMaskStyle: React.CSSProperties = {
		// @ts-ignore
		'--fade': '6rem',
		WebkitMaskImage: `
		  linear-gradient(
			to bottom,
			transparent 0,
			black var(--fade),
			black calc(100% - var(--fade)),
			transparent 100%
		  )`,
		maskImage: `
		  linear-gradient(
			to bottom,
			transparent 0,
			black var(--fade),
			black calc(100% - var(--fade)),
			transparent 100%
		  )`,
	}

	const motionTransition = {
		duration: 0.5,
		ease: 'easeInOut',
	}

	const containerVariants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
			},
		},
	}

	const handleOpenWordDetailsModal = (libraryVocabulary: LibraryVocabulary) => {
		setData(libraryVocabulary)
		setMode('saved/no-edit')
		showModal()
	}

	const dismissTop = async () => {
		if (cards.length > 0) {
			setCards((prev) => prev.slice(1))

			if (cards.length === 1) {
				await onLastCardSubmitted(answers)
			}
		}
	}

	const answerSelected = (card: PracticeSetItemChoice, choice: string) => {
		if (answers[card.id]) {
			if (card.vocabulary.word === choice) {
				handleOpenWordDetailsModal(card.vocabulary)
			}
			return
		}
		const cardAnswer = {
			[card.id]: {
				id: card.id,
				selected: choice,
				correct: card.vocabulary.word,
			},
		}
		setAnswers((prev) => ({
			...prev,
			...cardAnswer,
		}))
	}

	useEffect(() => {
		if (Object.keys(answers).length !== 0) {
			const results = {} as Record<number, PracticeItemChoicesResult>
			for (const [iStr, item] of Object.entries(answers) as [string, CurrentCardSelection][]) {
				const i = Number(iStr)
				results[i] = {
					is_correct: item.selected === item.correct,
				} as PracticeItemChoicesResult
			}
			// @ts-ignore
			setAnswerResult({
				result: results,
			})
		}
	}, [answers])

	const onLastCardSubmitted = async (completedAnswers: Record<number, CurrentCardSelection>) => {
		// all answered, submit them
		setIsSubmittingAnswersLoading(true)
		await submitAnswers(practiceSet, completedAnswers)
			.then((result: ChoicesPracticeAnswer) => {
				setAnswerResult(result)
				onPracticeAnswersSubmitted()
			})
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setIsSubmittingAnswersLoading(false))
	}

	const submitAnswers = async (practiceSet: PracticeSet, completedAnswers: Record<number, CurrentCardSelection>) => {
		return await api(`/v2/practice/sets/${practiceSet.id}/answer`, {
			method: 'POST',
			body: {
				inputs: Object.fromEntries(
					Object.entries(completedAnswers).map(([id, selection]) => [
						id,
						{ answer: selection.selected === selection.correct, choice: selection.selected },
					]),
				),
			},
		})
	}

	return (
		<div className="relative flex flex-col w-full h-full overflow-hidden">
			<div className="px-8">
				<div className="flex flex-col justify-center gap-1.5">
					<div className="flex flex-col">
						<div className="flex items-center mt-2">
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
							<div className="px-3">{t('Choices')}</div>
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
						</div>
					</div>
					{currentPracticeStatus != 'finished' && (
						<>
							<div>
								<p className="text-sm">{t('Choose the correct translation for each word / term.')}</p>
							</div>
						</>
					)}
				</div>
			</div>
			<div
				className="relative w-full flex-1 overflow-hidden"
				style={
					!isSubmittingAnswersLoading && practiceSetItems.length > 0 && currentPracticeStatus !== 'finished'
						? fadeMaskStyle
						: undefined
				}>
				{isSubmittingAnswersLoading ? (
					<AnimatePresence mode="wait">
						<motion.div
							key="anki-submit-loading"
							className="flex flex-col justify-center items-center gap-2 h-full">
							<span>{t('Submitting')}</span>
							<Loader2 className="h-4 w-4 animate-spin" />
						</motion.div>
					</AnimatePresence>
				) : currentPracticeStatus === 'finished' && answerResult !== undefined ? (
					<AnimatePresence mode="wait">
						<motion.div
							className="flex flex-col w-full min-h-full"
							variants={containerVariants}
							initial="hidden"
							animate="show">
							<div className="flex flex-col items-center justify-center">
								<PracticeSetResultSummary
									correct_count={answerResult.correct_count}
									items_count={answerResult.items_count}
									practiced_at={answerResult.practiced_at || ''}
								/>
							</div>
							<LibraryVocabularyLearningsPracticeResult
								className="flex-1 justify-center"
								answerDetails={answerDetails}
								handleOpenWordDetailsModal={handleOpenWordDetailsModal}
							/>
						</motion.div>
					</AnimatePresence>
				) : practiceSetItems.length > 0 ? (
					<AnimatePresence mode="sync">
						{cards.map((card, index) => (
							<motion.div
								key={card.id}
								initial={{ y: index === 0 ? 0 : `-${cardOffset}%` }}
								animate={{ y: index === 0 ? 0 : `-${cardOffset}%` }}
								exit={{ y: `${cardOffset}%` }}
								transition={motionTransition}
								className={`absolute flex flex-col justify-center items-center ${insetStyles.tailwindClasses || ''}`}
								style={{
									zIndex: cards.length - index,
									...insetStyles.customStyles,
								}}>
								<div className="w-full relative rounded-lg">
									<div className="absolute dark:bg-black bg-input px-3 pb-3 pt-1 rounded -top-8 start-5">
										{cards.length}
									</div>
									<div className="w-full flex items-center justify-center text-center flex-1 font-bold text-base dark:bg-black bg-input rounded-t-lg py-3">
										{card.vocabulary.translation}
									</div>
									<div
										className={`flex flex-col gap-4 p-4 items-center justify-center w-full flex-[2] ${themeColor === 'dark' ? 'bg-[#3d3d3d]' : 'bg-[#e1e1e1]'} rounded-b-lg`}>
										{card.all_choices.map((choice, i) => (
											<Button
												key={i}
												onClick={() => answerSelected(card, choice)}
												className={`relative w-full h-auto text-base ring-2 dark:bg-black bg-card dark:text-white text-black ${answers[card.id] !== undefined ? 'active:bg-input dark:active:bg-black' : 'active:bg-[#e1e1e1] dark:active:bg-[#3d3d3d]'} ${answers[card.id]?.id === card.id ? (answers[card.id]?.correct === choice ? 'ring-appgreen' : 'ring-danred') : 'dark:ring-white ring-gray-400'}`}>
												{choice}
												{answers[card.id] &&
													(choice === card.vocabulary.word ? (
														<div className="absolute w-7 h-7 -top-2 -end-2 flex justify-center items-center bg-appgreen dark:text-black text-white rounded-[50%]">
															<CircleCheckBig className="w-5 h-5 rotate-6" />
														</div>
													) : (
														answers[card.id].selected === choice && (
															<div className="absolute w-7 h-7 -top-2 -end-2 flex justify-center items-center bg-danred dark:text-black text-white rounded-[50%]">
																<CircleX className="w-5 h-5 rotate-6" />
															</div>
														)
													))}
											</Button>
										))}
									</div>
								</div>
								<div className={`${answers[card.id] === undefined ? 'invisible' : ''}`}>
									<Button
										onClick={dismissTop}
										variant="default"
										className="flex justify-center items-center mt-4">
										{t('Continue')}
									</Button>
								</div>
							</motion.div>
						))}
					</AnimatePresence>
				) : (
					<></>
				)}
			</div>
			<div className="h-[6.2rem] flex flex-row items-center justify-around pb-2" dir="ltr">
				<div className="flex w-full h-full items-end justify-end mx-1">
					<AnkiResultCardStack
						side="left"
						cards={Array(answerDetails.incorrectCount)}
						rotateGap={1}
						startX={0}
						gapX={13}
						gapY={1.5}
						h="h-[4.3rem]"
					/>
				</div>
				<div className="flex w-full h-full items-end justify-start mx-1">
					<AnkiResultCardStack
						side="right"
						cards={Array(answerDetails.correctCount)}
						rotateGap={1}
						startX={0}
						gapX={13}
						gapY={1.5}
						h="h-[4.3rem]"
					/>
				</div>
			</div>
			<div className="flex flex-col items-center justify-center select-none">
				<small>
					{answerDetails.correctCount > 0 || answerDetails.incorrectCount > 0
						? t('Incorrect / Correct guesses')
						: ''}
				</small>
			</div>
			{currentPracticeStatus === 'finished' && answerResult !== undefined && (
				<div className="w-full flex flex-col justify-center items-center gap-3 pb-4">
					<Button onClick={onClosePracticeSet} className="h-6 px-5 mt-2" variant="default">
						{availableAppLanguages[appLanguageCode].dir === 'rtl' ? (
							<ChevronRight className="h-4 w-4" />
						) : (
							<ChevronLeft className="h-4 w-4" />
						)}
						{t('Return to overview')}
					</Button>
				</div>
			)}
		</div>
	)
}

export default ChoicesPractice
